import {useMutation, useQuery} from "react-query"
import {ApiResponse} from "dto/ApiResponse"
import {User} from "dto/User"
import defaultAxios from "lib/http/http.common"
import {queryClient} from "index"
import {Role} from "dto/Role"
import {Group} from "dto/Group"
import {UserSession} from "dto/Session"

const FindAllUserQueryKey = "findAllUsers"
const FindUserById = "findByUserById"
const FindUserRolesById = "findByUserRolesById"
const FindUserAvailableRoles = "findAvailableRolesById"
const FindUserGroupsById = "findUserGroupsById"
const FindUserSessionById = "findUserSessionById"
export const useFindUsers = () => {
    return useQuery(FindAllUserQueryKey, async () => {
        const response = await defaultAxios.get<ApiResponse<User[]>>("/user")
        return response.data?.body
    })
}

export const useFindUserById = (id: string | undefined) => {
    return useQuery(FindUserById, async () => {
        const response = await defaultAxios.get<ApiResponse<User>>(`/user/${id}`)
        return response.data?.body
    }, {
        enabled: !!id
    })
}

export const useFindUserAvailableRoles = (id: string | undefined) => {
    return useQuery(FindUserAvailableRoles, async () => {
        const response = await defaultAxios.get<ApiResponse<Role[]>>(`/user/${id}/realm/roles/available`)
        return response.data?.body
    }, {
        enabled: !!id
    })
}
export const useFindUserRolesById = (id: string | undefined) => {
    return useQuery(FindUserRolesById, async () => {
        const response = await defaultAxios.get<ApiResponse<Role[]>>(`/user/${id}/realm/roles`)
        return response.data?.body
    }, {
        enabled: !!id
    })
}

export const useFindGroupsIncludingUser = (id: string | undefined) => {
    return useQuery(FindUserGroupsById, async () => {
        const response = await defaultAxios.get<ApiResponse<Group[]>>(`/user/${id}/groups`)
        return response.data?.body
    }, {
        enabled: !!id
    })
}

export const useFindUserSessionById = (id: string | undefined) => {
    return useQuery([FindUserSessionById, id], async () => {
        const response = await defaultAxios.get<ApiResponse<UserSession[]>>(`/user/${id}/sessions`)
        return response.data?.body
    }, {
        enabled: !!id
    })
}

export const useDeleteSessionByIndex = () => {
    return useMutation(async ({id, index}: { id: string, index: number }) => {
        await defaultAxios.delete(`/user/${id}/sessions/${index}`)
        await queryClient.invalidateQueries([FindUserSessionById, id])
    })
}

export const useDeleteUserById = () => {
    return useMutation(async (id: string) => {
        await defaultAxios.delete(`/user/${id}`)
        await queryClient.invalidateQueries(FindAllUserQueryKey)
    })
}

export const useRegisterUser = () => {
    return useMutation(async (user: User) => {
        await defaultAxios.post("/user", user)
        await queryClient.invalidateQueries(FindAllUserQueryKey)
    })
}

export const useUpdateUser = () => {
    return useMutation(async (user: User) => {
        await defaultAxios.put("/user", user)
        await queryClient.invalidateQueries(FindAllUserQueryKey)
        await queryClient.invalidateQueries(FindUserById)
    })
}

export const useUpdateUserAttribute = () => {
    return useMutation(async (user: User) => {
        await defaultAxios.put("/user/attribute", {
            id: user.id,
            attributes: user.attributes
        })
        await queryClient.invalidateQueries(FindAllUserQueryKey)
        await queryClient.invalidateQueries(FindUserById)
    })
}

export const useUpdateUserPassword = () => {
    return useMutation(async ({id, password}: { id: string, password: string }) => {
        await defaultAxios.put("/user/password", {
            id: id,
            password,
        })
        await queryClient.invalidateQueries(FindAllUserQueryKey)
        await queryClient.invalidateQueries([FindUserById, id])
    })
}

export const useAssignRoleToUser = () => {
    return useMutation(async ({id, roleName}: { id: string, roleName: string }) => {
        await defaultAxios.post(`/user/${id}/role/${roleName}`,)
        await queryClient.invalidateQueries(FindUserAvailableRoles)
        await queryClient.invalidateQueries(FindUserRolesById)
        await queryClient.invalidateQueries([FindUserById, id])
    })
}
export const useRemoveRoleFromUser = () => {
    return useMutation(async ({id, roleName}: { id: string, roleName: string }) => {
        await defaultAxios.delete(`/user/${id}/role/${roleName}`,)
        await queryClient.invalidateQueries(FindUserAvailableRoles)
        await queryClient.invalidateQueries(FindUserRolesById)
        await queryClient.invalidateQueries([FindUserById, id])
    })
}

export const useAssignToGroup = () => {
    return useMutation(async ({id, groupId}: { id: string, groupId: string }) => {
        await defaultAxios.post(`/user/${id}/group/${groupId}`,)
        await queryClient.invalidateQueries(FindUserGroupsById)
    })
}

export const useLeaveGroup = () => {
    return useMutation(async ({id, groupId}: { id: string, groupId: string }) => {
        await defaultAxios.delete(`/user/${id}/group/${groupId}`,)
        await queryClient.invalidateQueries(FindUserGroupsById)
    })
}

